/**
 * FinancialPaymentMethods - Página de Meios de Pagamento do módulo Financeiro
 * 
 * Layout premium padronizado com MetricCards, filtros inline e tabela
 * CRUD completo com listagem, criação, edição e soft delete
 * 
 * @author Sistema
 * @version 2.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação da página
 * RELEASE: 2026-03-19 v2.0.0 - Layout premium padronizado (tabela, métricas, filtros)
 */

import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { 
  Plus, 
  Search,
  Edit, 
  Trash2, 
  Wallet, 
  CreditCard,
  MoreHorizontal,
  CheckCircle,
  Banknote,
  Layers,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  useFinanceiroMeiosPagamento, 
  MeioPagamentoFormData, 
  FinanceiroMeioPagamento, 
  TIPOS_PAGAMENTO,
  TipoPagamento
} from '@/hooks/useFinanceiroMeiosPagamento';
import { useFinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import { usePermissions } from '@/hooks/usePermissions';

// ============================================================
// COMPONENTES AUXILIARES - Padrão Premium CRM
// ============================================================

/** Estilos semânticos para MetricCard - padrão horizontal compacto */
const METRIC_CARD_STYLES = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Props do MetricCard */
interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  variant: keyof typeof METRIC_CARD_STYLES;
}

/**
 * Card de métrica horizontal compacto — padrão unificado
 */
function MetricCard({ title, value, icon: Icon, variant }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Props do EmptyState */
interface EmptyStateProps {
  title: string;
  description: string;
  icon: LucideIcon;
  action?: { label: string; onClick: () => void };
}

/**
 * Estado vazio premium com borda pontilhada e gradiente
 */
function EmptyState({ title, description, icon: Icon, action }: EmptyStateProps) {
  return (
    <div className="relative border-dashed border-2 border-border/50 rounded-xl bg-gradient-to-br from-muted/30 via-background to-muted/10 p-12 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">{description}</p>
      {action && (
        <Button size="sm" onClick={action.onClick}>
          <Plus className="h-4 w-4 mr-2" />
          {action.label}
        </Button>
      )}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

export default function FinancialPaymentMethods() {
  const { meios, isLoading, createMeio, updateMeio, deleteMeio } = useFinanceiroMeiosPagamento();

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'financial_payment_methods';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  const { contas } = useFinanceiroContaCorrente();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMeio, setSelectedMeio] = useState<FinanceiroMeioPagamento | null>(null);
  const [formData, setFormData] = useState<MeioPagamentoFormData>({
    nome: '',
    tipo_pagamento: 'pix',
    conta_corrente_id: null,
    instrucoes: '',
  });

  // Filtros
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState<string>('all');

  // ============================================================
  // MÉTRICAS - calculadas via useMemo
  // ============================================================
  const stats = useMemo(() => {
    const activeCount = meios.filter(m => m.is_active).length;
    const pixCount = meios.filter(m => m.tipo_pagamento === 'pix').length;
    const otherCount = meios.filter(m => m.tipo_pagamento !== 'pix').length;
    return {
      total: meios.length,
      active: activeCount,
      pix: pixCount,
      other: otherCount,
    };
  }, [meios]);

  // ============================================================
  // DADOS FILTRADOS
  // ============================================================
  const filteredMeios = useMemo(() => {
    return meios.filter(m => {
      // Filtro por busca (nome)
      if (search) {
        const term = search.toLowerCase();
        if (!m.nome.toLowerCase().includes(term)) return false;
      }
      // Filtro por tipo de pagamento
      if (filterTipo !== 'all' && m.tipo_pagamento !== filterTipo) return false;
      return true;
    });
  }, [meios, search, filterTipo]);

  // ============================================================
  // HANDLERS - lógica original preservada
  // ============================================================

  /** Busca label do tipo de pagamento */
  const getTipoLabel = (tipo: string) => {
    return TIPOS_PAGAMENTO.find(t => t.value === tipo)?.label || tipo;
  };

  /** Busca nome da conta vinculada */
  const getContaName = (contaId: string | null) => {
    if (!contaId) return null;
    return contas.find(c => c.id === contaId)?.nome || null;
  };

  const handleNew = () => {
    setSelectedMeio(null);
    setFormData({ nome: '', tipo_pagamento: 'pix', conta_corrente_id: null, instrucoes: '' });
    setFormOpen(true);
  };

  const handleEdit = (meio: FinanceiroMeioPagamento) => {
    setSelectedMeio(meio);
    setFormData({
      nome: meio.nome,
      tipo_pagamento: meio.tipo_pagamento,
      conta_corrente_id: meio.conta_corrente_id,
      instrucoes: meio.instrucoes || '',
    });
    setFormOpen(true);
  };

  const handleSave = () => {
    if (selectedMeio) {
      updateMeio.mutate({ id: selectedMeio.id, ...formData });
    } else {
      createMeio.mutate(formData);
    }
    setFormOpen(false);
  };

  /**
   * Handler de exclusão com validação de vínculos
   * Verifica cobranças vinculadas antes de permitir exclusão
   */
  const handleDeleteMeio = async (meio: FinanceiroMeioPagamento) => {
    // Verificar cobranças vinculadas ao meio de pagamento
    const { count } = await supabase
      .from('financeiro_cobrancas')
      .select('*', { count: 'exact', head: true })
      .eq('meio_pagamento_id', meio.id)
      .is('deleted_at', null);

    if (count && count > 0) {
      // Possui vínculos — informar e bloquear exclusão
      await confirmDialog({
        variant: 'info',
        title: 'Não é possível remover',
        description: `O meio de pagamento "${meio.nome}" possui ${count} cobrança(s) vinculada(s). Desative-o em vez de excluir.`,
        confirmText: 'Entendi',
      });
    } else {
      // Sem vínculos — confirmar exclusão
      const confirmed = await confirmDialog({
        variant: 'danger',
        title: 'Remover meio de pagamento?',
        description: `Tem certeza que deseja excluir "${meio.nome}"? Esta ação não pode ser desfeita.`,
        confirmText: 'Remover',
      });
      if (confirmed) {
        deleteMeio.mutate(meio.id);
      }
    }
  };

  // Verificar se há filtros ativos
  const hasActiveFilters = !!search || filterTipo !== 'all';

  return (
    <div className="flex flex-col h-full">
      {/* ============================================================ */}
      {/* HEADER COMPACTO - Padrão premium */}
      {/* ============================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Meios de Pagamento</h1>
          <p className="text-xs text-muted-foreground">
            Gerencie as formas de pagamento aceitas
          </p>
        </div>
        {hasCreatePermission && (
          <Button 
            size="xs" 
            onClick={handleNew}
            className=""
          >
            <Plus className="mr-1.5" />
            Novo Meio
          </Button>
        )}
      </div>

      {/* ============================================================ */}
      {/* KPI CARDS - Padrão premium */}
      {/* ============================================================ */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
          <MetricCard title="Total de Meios" value={stats.total} icon={Layers} variant="primary" />
          <MetricCard title="Ativos" value={stats.active} icon={CheckCircle} variant="success" />
          <MetricCard title="PIX" value={stats.pix} icon={Banknote} variant="info" />
          <MetricCard title="Outros Tipos" value={stats.other} icon={CreditCard} variant="purple" />
        </div>
      )}

      {/* ============================================================ */}
      {/* BARRA DE FILTROS - Padrão premium */}
      {/* ============================================================ */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Filtro por tipo de pagamento */}
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-[160px] h-8 text-xs">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {TIPOS_PAGAMENTO.map(tipo => {
              const IconComp = PaymentMethodIcon[tipo.value] || DefaultPaymentIcon;
              return (
                <SelectItem key={tipo.value} value={tipo.value}>
                  <span className="flex items-center gap-2">
                    <IconComp className="h-4 w-4 shrink-0" />
                    {tipo.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Busca */}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Indicador de carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse ml-auto">
            Atualizando...
          </span>
        )}
      </div>

      {/* ============================================================ */}
      {/* CONTEÚDO - TABELA DE MEIOS DE PAGAMENTO */}
      {/* ============================================================ */}
      <div className="flex-1 overflow-auto">
        <Card className="border-border/50 overflow-hidden bg-gradient-to-br from-background to-muted/10">
          {/* Header da tabela */}
          <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-foreground">Lista de Meios</h2>
              <Badge variant="secondary" className="font-normal">
                {filteredMeios.length} encontrado{filteredMeios.length !== 1 ? 's' : ''}
              </Badge>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>

          {/* Conteúdo */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredMeios.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={hasActiveFilters ? 'Nenhum meio encontrado' : 'Nenhum meio de pagamento cadastrado'}
                description={hasActiveFilters
                  ? 'Tente buscar por outro termo ou limpe os filtros.'
                  : 'Cadastre suas formas de pagamento para começar.'
                }
                icon={Wallet}
                action={!hasActiveFilters && hasCreatePermission ? { label: 'Cadastrar Meio', onClick: handleNew } : undefined}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/40">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Conta Vinculada</TableHead>
                    <TableHead className="font-semibold">Instruções</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMeios.map((meio) => (
                    <TableRow 
                      key={meio.id}
                      className="group hover:bg-muted/20 transition-colors"
                    >
                      {/* Nome */}
                      <TableCell>
                        <p className="font-medium">{meio.nome}</p>
                      </TableCell>

                      {/* Tipo */}
                      <TableCell>
                        {(() => {
                          const IconComp = PaymentMethodIcon[meio.tipo_pagamento] || DefaultPaymentIcon;
                          return (
                            <Badge variant="outline" className="inline-flex items-center gap-1.5">
                              <IconComp className="h-3.5 w-3.5 shrink-0" />
                              {getTipoLabel(meio.tipo_pagamento)}
                            </Badge>
                          );
                        })()}
                      </TableCell>

                      {/* Conta Vinculada */}
                      <TableCell>
                        {meio.conta_corrente_id ? (
                          <span className="text-sm">{getContaName(meio.conta_corrente_id) || '—'}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      {/* Instruções */}
                      <TableCell>
                        {meio.instrucoes ? (
                          <p className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]">
                            {meio.instrucoes}
                          </p>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      {/* Status — padrão unificado (Badge outline + StatusDot) */}
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={meio.is_active ? 'emerald' : 'muted'} />
                          {meio.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>

                      {/* Ações */}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {hasEditPermission && (
                              <DropdownMenuItem onClick={() => handleEdit(meio)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {hasDeletePermission && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteMeio(meio)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* ============================================================ */}
      {/* MODAL DE CRIAÇÃO/EDIÇÃO - Lógica original preservada */}
      {/* ============================================================ */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedMeio ? 'Editar Meio de Pagamento' : 'Novo Meio de Pagamento'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={e => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: PIX Banco do Brasil"
              />
            </div>
            <div>
              <Label htmlFor="tipo_pagamento">Tipo de Pagamento *</Label>
              <Select
                value={formData.tipo_pagamento}
                onValueChange={value => setFormData({ ...formData, tipo_pagamento: value as TipoPagamento })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_PAGAMENTO.map(tipo => {
                    const IconComp = PaymentMethodIcon[tipo.value] || DefaultPaymentIcon;
                    return (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <span className="flex items-center gap-2">
                          <IconComp className="h-4 w-4 shrink-0" />
                          {tipo.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="conta_corrente_id">Conta Corrente (opcional)</Label>
              <Select
                value={formData.conta_corrente_id || 'none'}
                onValueChange={value => setFormData({ ...formData, conta_corrente_id: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Vincular a uma conta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {contas.map(conta => (
                    <SelectItem key={conta.id} value={conta.id}>
                      {conta.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="instrucoes">Instruções de Pagamento</Label>
              <Textarea
                id="instrucoes"
                value={formData.instrucoes}
                onChange={e => setFormData({ ...formData, instrucoes: e.target.value })}
                placeholder="Instruções adicionais para este meio de pagamento"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleSave} 
              disabled={!formData.nome || !formData.tipo_pagamento || createMeio.isPending || updateMeio.isPending}
            >
              {createMeio.isPending || updateMeio.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ConfirmDialog premium (renderizado pelo hook useConfirmDialog) */}
      {ConfirmDialogComponent}
    </div>
  );
}
